import React from 'react';
import {
  StatusRequested,
  StatusAprooved,
  StatusConfirmed,
  StatusDelivered,
  StatusDisapproved,
  StatusEliminated,
  StatusExpired,
  StatusModifiedConfirmed,
  StatusNotDelivered,
  StatusRead,
  StatusRejected,
  StatusReturned,
  StatusSent,
  StatusShipped,
  StatusValidated
} from 'assets/icons';
import theme from 'common/styles/extendeal-theme';
import i18n from 'common/language/i18n';
import styled from 'styled-components';
import Typography from '@material-ui/core/Typography';
import { rem, rgba } from 'polished';

export interface StatusBadgeProps {
  status:
    | 'requested'
    | 'aprooved'
    | 'approved'
    | 'disapproved'
    | 'expired'
    | 'sent'
    | 'read'
    | 'eliminated'
    | 'confirmed'
    | 'modified_confirmed'
    | 'rejected'
    | 'shipped'
    | 'ordered'
    | 'not_delivered'
    | 'delivered'
    | 'validated'
    | 'in_progress'
    | 'partial_output'
    | 'full_output'
    | 'error'
    | 'connection_error'
    | 'returned';
  variant?: 'default' | 'uniform' | 'pill';
}

interface StatusIconProps {
  Svg: React.FC<React.SVGProps<SVGSVGElement>>;
  color: string;
}

const Container = styled.div<Omit<StatusBadgeProps, 'status'> & { color: string }>`
  position: relative;
  display: flex;
  height: ${rem('20px')};
  color: ${props => (props.variant === 'uniform' ? props.color : props.theme.colors.S['800'])};
  font-size: ${rem('12px')};
  align-items: center;
`;

/** SCI summary: pill + leading dot. */
const PillContainer = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${rem('6px')};
  width: max-content;
  max-width: none;
  box-sizing: border-box;
  flex-shrink: 0;
  padding: ${rem('3px')} ${rem('10px')};
  border-radius: 999px;
  background: ${props => rgba(props.$color, 0.16)};
  color: ${props => props.$color};

  body[data-theme='dark'] & {
    background: ${props => rgba(props.$color, 0.18)};
  }
  font-size: ${rem('12px')};
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  overflow: visible;
`;

const PillDot = styled.span`
  display: inline-block;
  width: ${rem('6px')};
  height: ${rem('6px')};
  border-radius: 50%;
  flex-shrink: 0;
  background: currentColor;
`;

const PillLabel = styled.span`
  flex: 0 0 auto;
  white-space: nowrap;
`;

/** Status accent colors — same hex in light and dark (used by pills + activity timeline). */
const pillColors: Record<string, string> = {
  requested: '#D08900',
  ordered: '#448aff',
  validated: '#4dd0e1',
  in_progress: '#ffb425',
  read: '#ba68c8',
  partial_output: '#9575cd',
  full_output: '#9ccc65',
  confirmed: '#43A047',
  modified_confirmed: '#689f38',
  approved: '#448aff',
  aprooved: '#448aff',
  delivered: '#0097a7',
  disapproved: '#FF3030',
  rejected: '#FF3030',
  returned: '#e69deb',
  not_delivered: '#e57373',
  error: '#FF3030',
  connection_error: '#FF3030',
  expired: '#619fc9',
  sent: '#448aff',
  shipped: '#448aff',
  eliminated: '#FF3030'
};

const StatusLabel = styled(Typography)`
  font-weight: 400;
`;

const StatusIcon = ({ Svg, color }: StatusIconProps) => <Svg fill={color} height="20" />;

const colors = { ...pillColors };

const states = {
  requested: {
    label: i18n.t('shared:status.requested', { context: 'female' }),
    icon: <StatusIcon Svg={StatusRequested} color={colors.requested} />
  },
  aprooved: {
    label: i18n.t('shared:status.approved', { context: 'female' }),
    icon: <StatusIcon Svg={StatusAprooved} color={colors.aprooved} />
  },
  approved: {
    label: i18n.t('shared:status.approved', { context: 'female' }),
    icon: <StatusIcon Svg={StatusAprooved} color={colors.approved} />
  },
  disapproved: {
    label: i18n.t('shared:status.disapproved', { context: 'female' }),
    icon: <StatusIcon Svg={StatusDisapproved} color={colors.disapproved} />
  },
  expired: {
    label: i18n.t('shared:status.expired', { context: 'female' }),
    icon: <StatusIcon Svg={StatusExpired} color={colors.expired} />
  },
  sent: {
    label: i18n.t('shared:status.sent', { context: 'female' }),
    icon: <StatusIcon Svg={StatusSent} color={colors.sent} />
  },
  read: {
    label: i18n.t('shared:status.read', { context: 'female' }),
    icon: <StatusIcon Svg={StatusRead} color={colors.read} />
  },
  eliminated: {
    label: i18n.t('shared:status.eliminated', { context: 'female' }),
    icon: <StatusIcon Svg={StatusEliminated} color={colors.eliminated} />
  },
  confirmed: {
    label: i18n.t('shared:status.confirmed', { context: 'female' }),
    icon: <StatusIcon Svg={StatusConfirmed} color={colors.confirmed} />
  },
  modified_confirmed: {
    label: i18n.t('shared:status.modified_confirmed', { context: 'female' }),
    icon: <StatusIcon Svg={StatusModifiedConfirmed} color={colors.modified_confirmed} />
  },
  rejected: {
    label: i18n.t('shared:status.rejected', { context: 'female' }),
    icon: <StatusIcon Svg={StatusRejected} color={colors.rejected} />
  },
  shipped: {
    label: i18n.t('shared:status.shipped', { context: 'female' }),
    icon: <StatusIcon Svg={StatusShipped} color={colors.shipped} />
  },
  ordered: {
    label: i18n.t('shared:status.ordered', { context: 'female' }),
    icon: <StatusIcon Svg={StatusSent} color={colors.ordered} />
  },
  not_delivered: {
    label: i18n.t('shared:status.not_delivered', { context: 'female' }),
    icon: <StatusIcon Svg={StatusNotDelivered} color={colors.not_delivered} />
  },
  delivered: {
    label: i18n.t('shared:status.delivered', { context: 'female' }),
    icon: <StatusIcon Svg={StatusDelivered} color={colors.delivered} />
  },
  validated: {
    label: i18n.t('shared:status.validated', { context: 'female' }),
    icon: <StatusIcon Svg={StatusValidated} color={colors.validated} />
  },
  returned: {
    label: i18n.t('shared:status.returned', { context: 'female' }),
    icon: <StatusIcon Svg={StatusReturned} color={colors.returned} />
  },
  in_progress: {
    label: i18n.t('shared:status.in_progress', { context: 'female' }),
    icon: <StatusIcon Svg={StatusSent} color={colors.in_progress} />
  },
  partial_output: {
    label: i18n.t('shared:status.partial_output', { context: 'female' }),
    icon: <StatusIcon Svg={StatusShipped} color={colors.partial_output} />
  },
  full_output: {
    label: i18n.t('shared:status.full_output', { context: 'female' }),
    icon: <StatusIcon Svg={StatusDelivered} color={colors.full_output} />
  },
  error: {
    label: i18n.t('shared:status.error', { context: 'female' }),
    icon: <StatusIcon Svg={StatusDisapproved} color={colors.error} />
  },
  connection_error: {
    label: i18n.t('shared:status.connection_error', { context: 'female' }),
    icon: <StatusIcon Svg={StatusDisapproved} color={colors.connection_error} />
  }
};

export function StatusBadge({ status, variant }: StatusBadgeProps) {
  const state = status ? states[status as keyof typeof states] : undefined;
  const { icon, label } = state || {};
  const pillColor =
    (status && (pillColors[status] || colors[status as keyof typeof colors])) ||
    theme.colors.S['600'];

  if (variant === 'pill' && label) {
    return (
      <PillContainer $color={pillColor} title={label}>
        <PillDot />
        <PillLabel>{label}</PillLabel>
      </PillContainer>
    );
  }

  return (
    <Container variant={variant} color={status ? colors[status as keyof typeof colors] : theme.colors.S['800']}>
      {typeof variant !== typeof undefined && variant === 'uniform' ? (
        <>
          <StatusLabel
            variant="inherit"
            color="inherit"
            style={{ fontSize: '16px', paddingRight: '8px' }}
          >
            {label}
          </StatusLabel>
          {icon}
        </>
      ) : (
        <>
          {icon}
          <StatusLabel
            variant="inherit"
            style={{ paddingLeft: '8px', color: status ? colors[status as keyof typeof colors] : undefined }}
          >
            {label}
          </StatusLabel>
        </>
      )}
    </Container>
  );
}

StatusBadge.Colors = colors;
StatusBadge.AccentColors = pillColors;
